import React, { useEffect, useRef, useState, lazy, Suspense } from 'react';
import {
  ArrowRight,
  Target,
  Clock,
  Calendar,
  Play,
  BarChart,
  X,
  ShieldCheck,
  Loader2,
  Info,
} from 'lucide-react';
import { track, updateConsent } from './track';
import { submitLead, captureTrackingParams } from './lib/submit';
import { isDisposableDomain } from './lib/disposable-domains';
// Eager: PreHeader é leve (1KB) e fica acima da dobra junto com o Hero.
import PreHeader from './sections/PreHeader';

// Lazy: tudo abaixo da dobra. Servidor (renderToPipeableStream)
// resolve todas as Suspense antes de pipar — HTML pré-renderizado fica
// completo. Cliente baixa cada chunk separado e hidrata seletivamente
// (React 19 mantém o HTML do SSR durante a hidratação).
const Conteudo = lazy(() => import('./sections/Conteudo'));
const ParaQuem = lazy(() => import('./sections/ParaQuem'));
const Aprendizado = lazy(() => import('./sections/Aprendizado'));
const Giovanni = lazy(() => import('./sections/Giovanni'));
const Bonus = lazy(() => import('./sections/Bonus'));
const FAQ = lazy(() => import('./sections/FAQ'));
const CTAFinal = lazy(() => import('./sections/CTAFinal'));
const Footer = lazy(() => import('./sections/Footer'));

// Placeholder com altura mínima pra evitar CLS durante o carregamento
// dos chunks lazy no cliente. Visualmente combina com o tema dark.
const SectionFallback: React.FC<{ minH?: string }> = ({ minH = '400px' }) => (
  <div style={{ minHeight: minH }} className="bg-[#131314]" aria-hidden />
);

type Country = {
  code: string;
  dial: string;
  name: string;
  flag: string;
  maxLen: number;
  format?: (digits: string) => string;
};

const COUNTRIES: Country[] = [
  {
    code: 'BR', dial: '+55', name: 'Brasil', flag: '🇧🇷', maxLen: 11,
    format: (d) => {
      if (d.length === 0) return '';
      if (d.length <= 2) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
      if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
      return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    }
  },
  {
    code: 'US', dial: '+1', name: 'Estados Unidos', flag: '🇺🇸', maxLen: 10,
    format: (d) => {
      if (d.length === 0) return '';
      if (d.length <= 3) return `(${d}`;
      if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
      return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    }
  },
  { code: 'PT', dial: '+351', name: 'Portugal', flag: '🇵🇹', maxLen: 9 },
  { code: 'ES', dial: '+34', name: 'Espanha', flag: '🇪🇸', maxLen: 9 },
  { code: 'AR', dial: '+54', name: 'Argentina', flag: '🇦🇷', maxLen: 10 },
  { code: 'MX', dial: '+52', name: 'México', flag: '🇲🇽', maxLen: 10 },
  { code: 'CL', dial: '+56', name: 'Chile', flag: '🇨🇱', maxLen: 9 },
  { code: 'CO', dial: '+57', name: 'Colômbia', flag: '🇨🇴', maxLen: 10 },
  { code: 'PY', dial: '+595', name: 'Paraguai', flag: '🇵🇾', maxLen: 9 },
  { code: 'UY', dial: '+598', name: 'Uruguai', flag: '🇺🇾', maxLen: 8 },
  { code: 'PE', dial: '+51', name: 'Peru', flag: '🇵🇪', maxLen: 9 },
  { code: 'GB', dial: '+44', name: 'Reino Unido', flag: '🇬🇧', maxLen: 10 },
  { code: 'CA', dial: '+1', name: 'Canadá', flag: '🇨🇦', maxLen: 10 },
  { code: 'FR', dial: '+33', name: 'França', flag: '🇫🇷', maxLen: 9 },
  { code: 'DE', dial: '+49', name: 'Alemanha', flag: '🇩🇪', maxLen: 11 },
  { code: 'IT', dial: '+39', name: 'Itália', flag: '🇮🇹', maxLen: 10 },
  { code: 'JP', dial: '+81', name: 'Japão', flag: '🇯🇵', maxLen: 10 },
  { code: 'AU', dial: '+61', name: 'Austrália', flag: '🇦🇺', maxLen: 9 },
];

const formatPhone = (value: string, country: Country): string => {
  const digits = value.replace(/\D/g, '').slice(0, country.maxLen);
  return country.format ? country.format(digits) : digits;
};

// Heurística: detecta strings que parecem geradas aleatoriamente
// (padrão comum em contas temporárias descartáveis)
const looksRandom = (s: string): boolean => {
  if (s.length < 6) return false;
  // Tem mistura de letras e números, sem vogais consecutivas formando palavra?
  const hasLetters = /[a-z]/i.test(s);
  const hasDigits = /\d/.test(s);
  if (!hasLetters || !hasDigits) return false;
  // Razão consoantes/vogais muito alta sugere randomness
  const vowels = (s.match(/[aeiou]/gi) || []).length;
  const consonants = (s.match(/[bcdfghjklmnpqrstvwxyz]/gi) || []).length;
  if (consonants > 0 && vowels / (consonants + vowels) < 0.2) return true;
  // 4+ consoantes seguidas (típico de random)
  if (/[bcdfghjklmnpqrstvwxyz]{5,}/i.test(s)) return true;
  return false;
};

const validateEmail = async (email: string): Promise<string | null> => {
  const trimmed = email.trim().toLowerCase();
  if (!trimmed) return 'Informe um e-mail.';

  // Formato RFC 5322 simplificado
  const formatRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i;
  if (!formatRegex.test(trimmed)) return 'E-mail com formato inválido.';

  const [local, domain] = trimmed.split('@');

  // Bloqueia padrões obviamente falsos
  if (local.length < 2) return 'E-mail inválido.';
  if (/^(test|teste|fake|falso|asdf|qwerty|abc|aaa|xxx|admin|user|email|noreply|no-reply)$/i.test(local)) {
    return 'Use seu e-mail real, por favor.';
  }
  if (/^(.)\1+$/.test(local)) return 'Use seu e-mail real, por favor.';

  // Bloqueia TLDs de teste
  if (/\.(test|example|invalid|localhost)$/.test(domain)) {
    return 'Domínio de e-mail inválido.';
  }

  // Mini-lista curada de ~150 domínios descartáveis (3KB inline).
  // Cobertura completa fica por conta do Disify (chamada server-side abaixo).
  if (isDisposableDomain(domain)) {
    return 'E-mails temporários não são aceitos. Use um e-mail real.';
  }

  // Heurística: username aleatório + domínio não-comum = provável temp mail
  const commonProviders = new Set([
    'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com', 'msn.com',
    'yahoo.com', 'yahoo.com.br', 'icloud.com', 'me.com', 'mac.com', 'proton.me',
    'protonmail.com', 'aol.com', 'zoho.com', 'gmx.com', 'mail.com', 'uol.com.br',
    'bol.com.br', 'terra.com.br', 'ig.com.br', 'globo.com', 'r7.com',
  ]);
  if (!commonProviders.has(domain) && looksRandom(local)) {
    return 'Parece um e-mail temporário. Use seu e-mail real.';
  }

  // Validação via API pública Disify (checa MX + blocklist server-side)
  try {
    const res = await fetch(`https://disify.com/api/email/${encodeURIComponent(trimmed)}`, {
      signal: AbortSignal.timeout(4000),
    });
    if (res.ok) {
      const data = await res.json();
      if (data.disposable === true) return 'E-mails temporários não são aceitos.';
      if (data.dns === false) return 'Domínio de e-mail não existe.';
      if (data.format === false) return 'E-mail com formato inválido.';
    }
  } catch {
    // Falha de rede não bloqueia — confiamos nas checagens locais
  }

  return null;
};

// ============================================================
// LANDING PAGE - EVENTO "DO ZERO AO MERCADO AMERICANO"
// ============================================================

const LandingPage: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', whatsapp: '' });
  const [country, setCountry] = useState<Country>(COUNTRIES[0]);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [whatsappError, setWhatsappError] = useState<string | null>(null);
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [showCookieConfig, setShowCookieConfig] = useState(false);
  const [cookieAnalytics, setCookieAnalytics] = useState(true);
  const [cookieMarketing, setCookieMarketing] = useState(true);

  useEffect(() => {
    // Captura parâmetros da URL (conta, um, dois, tres, quatro, cinco) e persiste em sessionStorage
    captureTrackingParams();

    if (typeof window === 'undefined') return;

    // Já decidiu? Não mostra de novo.
    const hasDecided =
      localStorage.getItem('cookiePreferences') !== null ||
      localStorage.getItem('cookieConsent') !== null;
    if (hasDecided) return;

    // Aparece após 8s OU primeira interação (scroll/click/touch/keydown).
    // 8s é depois da janela típica de medição do LCP no Lighthouse —
    // assim o banner não vira o "maior elemento" da pintura inicial.
    let triggered = false;
    const events = ['scroll', 'click', 'touchstart', 'keydown'];
    const trigger = () => {
      if (triggered) return;
      triggered = true;
      setShowCookieBanner(true);
      events.forEach((e) => window.removeEventListener(e, trigger));
      clearTimeout(timer);
    };
    const timer = setTimeout(trigger, 8000);
    events.forEach((e) => window.addEventListener(e, trigger, { passive: true, once: true }));

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, trigger));
    };
  }, []);

  const persistConsent = (analytics: boolean, marketing: boolean, action: string) => {
    const prefs = { analytics, marketing };
    localStorage.setItem('cookiePreferences', JSON.stringify(prefs));
    localStorage.setItem(
      'cookieConsent',
      analytics && marketing ? 'accepted' : (!analytics && !marketing ? 'declined' : 'configured'),
    );
    updateConsent(prefs);
    setShowCookieBanner(false);
    setShowCookieConfig(false);
    track(action, { analytics, marketing });
  };

  const acceptCookies = () => persistConsent(true, true, 'cookie_accept');
  const saveCookieConfig = () =>
    persistConsent(cookieAnalytics, cookieMarketing, 'cookie_config_save');

  const openCookieConfig = () => {
    setShowCookieConfig(true);
    track('cookie_config_open');
  };

  const scrollToSection = (id: string, location?: string) => {
    if (location) track('cta_click', { location, target: id });
    const element = document.getElementById(id);
    if (element) {
      const offset = 90;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submittingRef = useRef(false); // trava instantânea — impede duplo clique

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trava instantânea (useRef não depende do ciclo de render do React)
    if (submittingRef.current) return;

    // Validação do WhatsApp — exige exatamente maxLen dígitos para o país selecionado
    const whatsappDigits = formData.whatsapp.replace(/\D/g, '');
    if (whatsappDigits.length !== country.maxLen) {
      const msg = `O número deve ter exatamente ${country.maxLen} dígitos para ${country.name}.`;
      setWhatsappError(msg);
      track('form_error', { field: 'whatsapp', reason: msg });
      return;
    }
    setWhatsappError(null);

    submittingRef.current = true;
    setIsSubmitting(true);
    const err = await validateEmail(formData.email);
    if (err) {
      submittingRef.current = false;
      setIsSubmitting(false);
      setEmailError(err);
      track('form_error', { field: 'email', reason: err });
      return;
    }
    setEmailError(null);
    track('form_submit', { country: country.code });
    const fullPhone = `${country.dial} ${formData.whatsapp}`;
    const formName = 'Inscrição - Do Zero ao Mercado Americano (Código)';
    try {
      const enriched = await submitLead({
        formName,
        email: formData.email,
        whatsapp: fullPhone,
      });
      // Sucesso — empurra payload completo no dataLayer (GTM)
      // Inclui: email/phone (raw + sha256), geo (city/state/postal/lat/lng/country/timezone),
      // tracking (conta/um/dois/...), userAgent, referrer, pageUrl, etc.
      const { formName: _fn, ...rest } = enriched;
      track('elementor_form_success', {
        form_name: _fn,
        ...rest,
      });
      try {
        sessionStorage.setItem('lead_email', formData.email);
        sessionStorage.setItem('lead_whatsapp', fullPhone);
      } catch { }
      // Propaga identificação + tracking para a /obrigado via URL
      // (evita perder dados se sessionStorage falhar — ex: Safari em modo privado)
      const tracking = captureTrackingParams();
      const redirectParams = new URLSearchParams();
      redirectParams.set('email', formData.email);
      redirectParams.set('phone', fullPhone);
      redirectParams.set('data_hora', new Date().toISOString());
      for (const [k, v] of Object.entries(tracking)) if (v) redirectParams.set(k, v);
      window.location.href = `/pesquisa?${redirectParams.toString()}`;
    } catch {
      // Falha silenciosa — não exibe erro para o usuário.
      // O sendBeacon no submit.ts já tentou garantir a gravação.
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#131314] text-white font-sans selection:bg-flag-100 selection:text-flag-900 overflow-x-hidden">

      <PreHeader />

      <main>
        {/* ============================================ */}
        {/* HERO - Chamada + Formulario */}
        {/* ============================================ */}
        <section id="inscricao" className="pt-10 lg:pt-16 bg-[#131314] overflow-hidden relative border-b border-slate-800">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.04)_1px,_transparent_1px)] [background-size:18px_18px] opacity-40 pointer-events-none"></div>

          <div className="w-full flex flex-col lg:flex-row items-stretch relative z-10">

            {/* Text + Form side */}
            <div className="lg:w-1/2 flex flex-col justify-center space-y-5 lg:space-y-6 text-center lg:text-left px-5 sm:px-8 lg:pl-12 lg:pr-8 xl:pl-[160px] xl:pr-36 py-10 lg:py-16">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-950/50 border border-emerald-800/50 text-emerald-400 text-xs sm:text-sm font-bold uppercase tracking-wide w-fit mx-auto lg:mx-0">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                </span>
                Evento Online Exclusivo
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-[3.5rem] font-heading font-semibold text-white leading-[1.08] tracking-tight">
                <span className="block">Do Zero ao</span>
                <span className="block">
                  <span className="bg-gradient-to-r from-[#B31942] to-[#0A3161] text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg inline-block mt-2 whitespace-nowrap">Mercado Americano</span>
                </span>
              </h1>

              <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed">
                Aprenda o melhor método para <strong className="text-white">operar no maior mercado financeiro do mundo</strong> mesmo sem experiência, <strong className="text-white">morando no Brasil</strong>.
              </p>

              <div className="flex flex-wrap gap-x-4 sm:gap-x-5 gap-y-2 text-xs sm:text-sm text-slate-300 font-semibold justify-center lg:justify-start">
                <div className="flex items-center gap-1.5 sm:gap-2"><Calendar size={14} className="text-navy-300 sm:w-4 sm:h-4" /> 8, 9 e 10 de Junho</div>
                <div className="flex items-center gap-1.5 sm:gap-2"><Clock size={14} className="text-navy-300 sm:w-4 sm:h-4" /> 19:30h (Horário de Brasília)</div>
                <div className="flex items-center gap-1.5 sm:gap-2"><Play size={14} className="text-navy-300 sm:w-4 sm:h-4" /> 100% Online e Gratuito</div>
              </div>

              {/* Formulario de inscricao */}
              <form onSubmit={handleFormSubmit} className="bg-white/5 border border-slate-700/50 rounded-2xl p-5 sm:p-6 space-y-4 text-left">
                <p className="text-white font-bold text-lg">Garanta sua vaga agora:</p>
                <div>
                  <div className="input-glow-wrapper">
                    <input
                      type="email"
                      placeholder="Seu melhor e-mail"
                      value={formData.email}
                      onChange={e => {
                        setFormData({ ...formData, email: e.target.value });
                        if (emailError) setEmailError(null);
                      }}
                      onBlur={async e => {
                        if (e.target.value) setEmailError(await validateEmail(e.target.value));
                      }}
                      required
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'email-error' : undefined}
                      className="input-glow"
                    />
                  </div>
                  {emailError && (
                    <p id="email-error" className="text-red-400 text-xs mt-2 ml-1">{emailError}</p>
                  )}
                </div>
                <div>
                  <div className="input-glow-wrapper">
                    <div className="input-glow flex items-center gap-0 !p-0 overflow-hidden">
                      <select
                        aria-label="Código do país"
                        value={country.code}
                        onChange={e => {
                          const next = COUNTRIES.find(c => c.code === e.target.value) || COUNTRIES[0];
                          setCountry(next);
                          setFormData({ ...formData, whatsapp: '' });
                          setWhatsappError(null);
                        }}
                        className="bg-transparent text-white text-sm font-semibold pl-4 pr-2 py-3.5 outline-none cursor-pointer border-r border-slate-700/60 appearance-none"
                      >
                        {COUNTRIES.map(c => (
                          <option key={c.code} value={c.code} className="bg-[#1a1a1b]">
                            {c.dial} {c.name}
                          </option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        placeholder={country.code === 'BR' ? '(DDD) 99999-9999' : `Número (${country.maxLen} dígitos)`}
                        value={formData.whatsapp}
                        onChange={e => {
                          setFormData({ ...formData, whatsapp: formatPhone(e.target.value, country) });
                          if (whatsappError) setWhatsappError(null);
                        }}
                        onBlur={e => {
                          const digits = e.target.value.replace(/\D/g, '');
                          if (digits.length > 0 && digits.length !== country.maxLen) {
                            setWhatsappError(`O número deve ter exatamente ${country.maxLen} dígitos para ${country.name}.`);
                          }
                        }}
                        required
                        autoComplete="tel-national"
                        inputMode="numeric"
                        aria-invalid={!!whatsappError}
                        aria-describedby={whatsappError ? 'whatsapp-error' : undefined}
                        className="flex-1 bg-transparent text-white placeholder-slate-500 px-4 py-3.5 outline-none min-w-0"
                      />
                    </div>
                  </div>
                  {whatsappError && (
                    <p id="whatsapp-error" className="text-red-400 text-xs mt-2 ml-1">{whatsappError}</p>
                  )}
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-cta w-full py-4 rounded-xl shadow-[0_0_30px_rgba(34,197,94,0.3)] disabled:opacity-60 disabled:cursor-wait"
                >
                  {isSubmitting ? 'Validando...' : 'Quero Participar'}
                  {!isSubmitting && <ArrowRight size={20} />}
                </button>
                <div className="flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                  <Info size={14} className="text-pink-500 mt-0.5 shrink-0" />
                  <p>
                    Cadastre-se e entre no grupo de WhatsApp para receber o link das aulas. Ao clicar no botão, você aceita nossos Termos de Uso e Política de Privacidade, incluindo cookies e envio de comunicações.
                  </p>
                </div>
              </form>
            </div>

            {/* Image side */}
            <div className="lg:w-1/2 relative w-full">
              <div className="hidden lg:block absolute inset-y-0 left-0 w-36 bg-gradient-to-r from-[#131314] to-transparent z-10 pointer-events-none"></div>
              <div className="hidden lg:block absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#131314] to-transparent z-10 pointer-events-none"></div>
              <div className="hidden lg:block absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#131314] to-transparent z-10 pointer-events-none"></div>
              <div className="absolute inset-x-0 bottom-0 h-36 bg-gradient-to-t from-[#131314] to-transparent z-10 pointer-events-none"></div>
              <div className="lg:hidden absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#131314] to-transparent z-10 pointer-events-none"></div>

              <img
                src="/banners/hero.webp"
                srcSet="/banners/hero-mobile.webp 750w, /banners/hero.webp 1280w"
                sizes="(max-width: 1023px) 100vw, 50vw"
                alt="Giovanni - Do Zero ao Mercado Americano"
                width={1280} height={874}
                className="w-full h-auto lg:h-full object-cover object-center"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          </div>
        </section>


        <Suspense fallback={<SectionFallback minH="900px" />}><Conteudo /></Suspense>
        <Suspense fallback={<SectionFallback minH="600px" />}><ParaQuem /></Suspense>
        <Suspense fallback={<SectionFallback minH="700px" />}>
          <Aprendizado onCta={() => scrollToSection('inscricao', 'giovanni')} />
        </Suspense>
        <Suspense fallback={<SectionFallback minH="800px" />}><Giovanni /></Suspense>
        <Suspense fallback={<SectionFallback minH="500px" />}><Bonus /></Suspense>
        <Suspense fallback={<SectionFallback minH="500px" />}><FAQ /></Suspense>
        <Suspense fallback={<SectionFallback minH="400px" />}>
          <CTAFinal onCta={() => scrollToSection('inscricao', 'cta-final')} />
        </Suspense>
      </main>

      <Suspense fallback={<SectionFallback minH="300px" />}>
        <Footer onScrollTo={scrollToSection} />
      </Suspense>

      {/* Pré-loading: overlay translúcido com card centrado (página principal fica visível atrás) */}
      {isSubmitting && (
        <div
          role="status"
          aria-live="polite"
          aria-label="Carregando"
          style={{
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
          className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-md flex items-center justify-center px-4 sm:px-6 animate-in fade-in"
        >
          <div className="bg-[#1a1a1b] border border-slate-700/60 rounded-2xl shadow-2xl px-5 py-7 sm:px-8 sm:py-9 w-full max-w-sm sm:max-w-md text-center animate-in slide-in-from-bottom-4">
            <img
              src="/logo-mobile.webp"
              alt="Do Zero ao Mercado Americano"
              className="h-7 sm:h-9 w-auto object-contain mx-auto mb-6 opacity-90"
              loading="eager"
            />
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-950/50 border border-emerald-800/50 flex items-center justify-center mx-auto mb-5 shadow-[0_0_30px_rgba(34,197,94,0.25)]">
              <Loader2 size={26} className="animate-spin text-emerald-400 sm:w-7 sm:h-7" />
            </div>
            <h2 className="text-lg sm:text-xl md:text-2xl font-heading font-semibold text-white mb-2 leading-snug">
              Estamos garantindo sua vaga...
            </h2>
            <p className="text-slate-400 text-xs sm:text-sm leading-relaxed">
              Confirmando sua inscrição e preparando seus bônus exclusivos.<br className="hidden sm:inline" /> Não feche essa página.
            </p>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      {showCookieBanner && !showCookieConfig && (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          className="fixed bottom-0 left-0 right-0 md:bottom-6 md:left-auto md:right-6 md:max-w-md z-50 bg-white border-t border-slate-200 rounded-t-2xl md:border md:rounded-2xl shadow-2xl shadow-black/40 md:ring-1 md:ring-emerald-500/20 overflow-hidden animate-in fade-in slide-in-from-bottom-4"
        >
          <div className="p-5">
            <h3 className="text-slate-900 font-bold text-base mb-1">Utilizamos cookies</h3>
            <p className="text-slate-600 text-xs leading-relaxed">
              Usamos cookies para melhorar sua experiência, analisar o tráfego e personalizar conteúdo. Você pode aceitar todos, recusar os opcionais ou configurar suas preferências. Veja nossa <a href="/privacidade" className="text-emerald-700 underline hover:text-emerald-800">política de privacidade</a>.
            </p>
          </div>
          <div className="flex border-t border-slate-200">
            <button
              onClick={openCookieConfig}
              className="flex-1 px-3 py-3.5 bg-white hover:bg-slate-100 text-slate-700 text-sm font-semibold transition-all border-r border-slate-200"
            >
              Configurar
            </button>
            <button
              onClick={acceptCookies}
              className="flex-1 px-4 py-3.5 bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold transition-all"
            >
              Aceitar
            </button>
          </div>
        </div>
      )}

      {/* Cookie Config Modal */}
      {showCookieConfig && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Configurar cookies"
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 backdrop-blur-sm animate-in fade-in"
        >
          <div
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 max-h-[92vh] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200">
              <div>
                <h3 className="text-slate-900 font-bold text-base">Preferências de cookies</h3>
                <p className="text-slate-500 text-xs mt-0.5">Você decide quais categorias permitir.</p>
              </div>
              <button
                onClick={() => setShowCookieConfig(false)}
                aria-label="Fechar"
                className="text-slate-400 hover:text-slate-700 transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-5 space-y-3">
              {/* Essenciais — sempre on */}
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2.5">
                    <ShieldCheck size={18} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-slate-900 font-bold text-sm">Essenciais</p>
                      <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                        Necessários para o funcionamento do site (sessão, segurança, preferências). Sempre ativos.
                      </p>
                    </div>
                  </div>
                  <span className="shrink-0 inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
                    Sempre on
                  </span>
                </div>
              </div>

              {/* Analytics — toggle */}
              <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-2.5">
                  <BarChart size={18} className="text-slate-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-900 font-bold text-sm">Analytics</p>
                    <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                      Nos ajudam a entender como você usa o site (Google Analytics) para melhorarmos o conteúdo.
                    </p>
                  </div>
                </div>
                <span
                  role="switch"
                  aria-checked={cookieAnalytics}
                  onClick={(e) => { e.preventDefault(); setCookieAnalytics(v => !v); }}
                  className={`shrink-0 relative inline-flex h-4 w-7 items-center rounded-full transition-colors border ${cookieAnalytics ? 'bg-white border-slate-400' : 'bg-slate-100 border-slate-300'}`}
                >
                  <span className={`inline-block h-2.5 w-2.5 transform rounded-full transition-all ${cookieAnalytics ? 'bg-slate-900 translate-x-3.5' : 'bg-slate-400 translate-x-0.5'}`} />
                </span>
              </label>

              {/* Marketing — toggle */}
              <label className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 p-4 cursor-pointer hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-2.5">
                  <Target size={18} className="text-slate-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-slate-900 font-bold text-sm">Marketing & Anúncios</p>
                    <p className="text-slate-600 text-xs mt-1 leading-relaxed">
                      Usados para mostrar anúncios mais relevantes em outras plataformas (Meta Ads, Google Ads).
                    </p>
                  </div>
                </div>
                <span
                  role="switch"
                  aria-checked={cookieMarketing}
                  onClick={(e) => { e.preventDefault(); setCookieMarketing(v => !v); }}
                  className={`shrink-0 relative inline-flex h-4 w-7 items-center rounded-full transition-colors border ${cookieMarketing ? 'bg-white border-slate-400' : 'bg-slate-100 border-slate-300'}`}
                >
                  <span className={`inline-block h-2.5 w-2.5 transform rounded-full transition-all ${cookieMarketing ? 'bg-slate-900 translate-x-3.5' : 'bg-slate-400 translate-x-0.5'}`} />
                </span>
              </label>
            </div>

            {/* Footer */}
            <div className="flex flex-col sm:flex-row gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={saveCookieConfig}
                className="flex-1 px-4 py-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-all"
              >
                Salvar preferências
              </button>
              <button
                onClick={acceptCookies}
                className="flex-1 px-4 py-3 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm font-bold transition-all"
              >
                Aceitar todos
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LandingPage;
